import { useEffect, useMemo, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { digitsOnly } from '@/lib/validators';
import { COUNTRIES, splitPhone, nsnLengthsFor, expectedLengthLabel } from '@/lib/phone-rules';

/**
 * PhoneInput — country-code dropdown (flag + dial code) + national number field.
 * Emits an E.164-style string ("+447911123456") via onChange, or "" when empty.
 *
 * Enter the national number WITHOUT the trunk "0" (e.g. 7911 123456 for the UK).
 */

const parsePhone = splitPhone;

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string | null;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  error,
  id,
  disabled,
  placeholder = '7911 123456',
  className,
}: Props) {
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [dial, setDial] = useState(parsed.dial);

  // Keep the selected dial in sync when the value's country changes externally.
  useEffect(() => {
    if (value) setDial(parsed.dial);
  }, [parsed.dial, value]);

  const national = parsed.national;
  // Emit with a space between the country code and the national number, e.g. "+44 7911123456".
  const emit = (d: string, nat: string) => onChange(nat ? `+${d} ${nat}` : '');

  // The cap follows the chosen country — it was hardcoded to 10, which silently truncated
  // the 11-digit countries (Germany, China) one digit short of a valid number.
  const allowedLengths = nsnLengthsFor(dial);
  const maxNational = allowedLengths.length ? Math.max(...allowedLengths) : 15;

  const ring = error ? 'border-red-400 focus-within:ring-red-400/40' : 'border-border focus-within:ring-brand-600/40';

  return (
    <div className={`w-full min-w-0 ${className ?? ''}`}>
      <div
        className={`flex h-10 items-center rounded-md border bg-card focus-within:ring-2 ${ring} ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        <div className="relative flex h-full items-center">
          <select
            aria-label="Country code"
            disabled={disabled}
            value={dial}
            onChange={(e) => {
              setDial(e.target.value);
              emit(e.target.value, national);
            }}
            className="h-full cursor-pointer appearance-none rounded-l-md border-0 bg-transparent pl-3 pr-5 text-sm text-foreground focus:outline-none"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.dial}>
                {c.flag} +{c.dial}
              </option>
            ))}
          </select>
          <CaretDown className="pointer-events-none absolute right-1 h-3 w-3 text-muted-foreground" />
        </div>
        <span className="h-5 w-px bg-border" aria-hidden />
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          disabled={disabled}
          value={national}
          maxLength={maxNational}
          onChange={(e) => emit(dial, digitsOnly(e.target.value).slice(0, maxNational))}
          onBlur={onBlur}
          placeholder={placeholder}
          // min-w-0 lets the field shrink inside a grid/flex column instead of
          // overflowing past its container on narrow layouts (e.g. the 2-col
          // invite modal); size={1} drops the default ~20ch intrinsic width.
          size={1}
          className="h-full w-full min-w-0 flex-1 rounded-r-md border-0 bg-transparent px-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
      </div>
      {error
        ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
        : national.length > 0 && !allowedLengths.includes(national.length) && (
            <p className="mt-1 text-xs text-muted-foreground">Number must be {expectedLengthLabel(dial)}</p>
          )}
    </div>
  );
}
